/**
 * Agent service for SDK-First Architecture.
 *
 * Agents are now read from optimizer_agent_registry (source of truth).
 * Performance metrics come from optimizer_agent_observations.
 */

import { api } from './api'

// SDK-compatible control declaration (target metric the agent optimizes)
export interface AgentControl {
  name: string
}

/**
 * Saved report ("data_report") used as an agent's bound Dataset.
 * Mirrors the shape returned by GET /data/reports (response.data.exports).
 */
export interface SavedReport {
  id: number
  name: string
  description?: string | null
  configuration?: {
    metrics?: string[]
    dimensions?: string[]
    pivot?: string[]
    period?: string[]
    conditional?: string
    [key: string]: any
  } | null
  created_at?: string | null
  updated_at?: string | null
}

/**
 * Agent interface matching OptimizerAgentRegistry schema.
 */
export interface Agent {
  id: number
  organization_id: number
  name: string
  slug: string | null  // agent_id in optimizer_agent_registry
  description: string | null
  agent_type: string | null
  status: string | null  // 'active' | 'inactive' | 'revoked'
  color: string | null  // Hex color (e.g., '#3b82f6')
  icon: string | null   // Icon name (e.g., 'users')
  objective_function: string | null  // LLM system prompt

  // SDK-First: Data configuration
  lookback_days: number  // Historical data period (1-365), default 30

  // Dataset: bound saved report (data_report). Optional — null means no dataset.
  dataset_report_id: number | null

  // Control: target metrics this agent optimizes
  control: AgentControl[] | null

  // Scheduling
  schedule_cron: string | null
  schedule_enabled: boolean | null  // false = manual only (no scheduled auto-runs)

  // Timestamps
  created_at: string | null
  updated_at: string | null
  last_seen_at: string | null  // Last activity timestamp

  // Optimizer tracking
  last_cycle_number: number  // Most recent cycle number
}

/**
 * Agent create request - Note: In SDK-First architecture, agents are typically
 * registered via SDK. This is for backwards compatibility/manual creation.
 */
export interface AgentCreateRequest {
  name: string
  description?: string
  color?: string
  icon?: string
  objective_function?: string

  // SDK-First: Data configuration
  lookback_days?: number

  // Dataset + Control
  dataset_report_id?: number | null
  control?: AgentControl[]

  // Scheduling
  schedule_cron?: string
}

/**
 * Agent update request matching OptimizerAgentRegistry updatable fields.
 */
export interface AgentUpdateRequest {
  name?: string
  description?: string
  color?: string
  icon?: string
  objective_function?: string

  // SDK-First: Data configuration
  lookback_days?: number

  // Dataset + Control
  dataset_report_id?: number | null
  control?: AgentControl[]

  // Scheduling and status
  schedule_cron?: string
  schedule_enabled?: boolean
  status?: string
}

// Organization model types
export interface OrganizationMetric {
  id: string
  description: string
  system: string
  sd?: string
  formula?: string
  aggregation?: string
}

export interface OrganizationDimension {
  id: string
  description: string
  sd?: string
}

export interface AgentRunResponse {
  message: string
  agent_id: number
  workflow_id: string
  note?: string
}

export interface AgentExecution {
  id: number
  agent_id: number
  organization_id: number
  cycle_id: string
  trigger_type: string
  status: string
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  llm_model_used: string | null
  tokens_input: number | null
  tokens_output: number | null
  llm_cost_usd: number | null
  decision: Record<string, any> | null
  error_message: string | null
}

export interface AgentAction {
  id: number
  execution_id: number
  agent_id: number
  action_type: string
  target: string | null
  parameters: Record<string, any> | null
  success: boolean
  result: Record<string, any> | null
  error_message: string | null
  user_status: 'pending' | 'applied' | 'dismissed' | 'skipped' | null
  resolved_at: string | null
  created_at: string
}

// Execution by cycle response (new format: agent_slug + cycle_number)
export interface ExecutionByCycleResponse {
  id: number
  agent_id: string  // This is the agent slug
  cycle_number: number
  status: string
  trigger_type: string | null
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  error_message: string | null
  llm_model: string | null
  tokens_input: number | null
  tokens_output: number | null
  llm_cost_usd: number | null
  effectiveness: number | null
  efficiency: number | null
  inputs: Record<string, any>[] | null  // Input data used by the agent for analysis
  decision: { data_context?: { data?: Record<string, any>[]; file_url?: string } } | null  // Decision data including raw data rows
  objectives: {
    // New cascader format
    objectives?: Array<{metric: string, operator: string, target: number | null}>
    constraints?: Array<{metric: string, operator: string, target: number | null}>
    search_space?: any
    upstream_status?: Record<string, string>
    // Legacy format
    objective_type?: string
    primary_metric?: string
    target_value?: number | null
  } | null
}

// DEPRECATED: PendingRecommendation, HandsUp, and HandsUpCounts types have been removed.
// Agents are now pure functions that only produce outputs. The Optimizer decides actions.

// Agent performance metrics from optimizer
export interface AgentPerformance {
  effectiveness: number | null
  efficiency: number | null
  effectiveness_trend: 'improving' | 'stable' | 'declining' | 'unknown'
  efficiency_trend: 'improving' | 'stable' | 'declining' | 'unknown'
  effectiveness_change: number | null
  efficiency_change: number | null
  last_cycle: number | null
  last_observed_at: string | null
  observation_count: number
}

// Optimizer Monitoring Types
export interface AgentStatusSummary {
  id: number
  agent_id: string
  name: string | null
  color: string | null
  icon: string | null
  organization_id: number
  organization_name: string | null
  status: string
  last_seen_at: string | null
  current_execution_status: string | null
  current_execution_started_at: string | null
  last_completed_at: string | null
  total_executions_24h: number
  successful_executions_24h: number
  failed_executions_24h: number
  pending_hands_up: number
}

export interface OptimizerMonitoringSummary {
  total_agents: number
  active_agents: number
  inactive_agents: number
  running_agents: number
  stuck_agents: number
  failed_recently: number
  pending_escalations: number
  executions_24h: number
  success_rate_24h: number | null
}

export interface ExecutionHistoryItem {
  id: number
  agent_id: string
  agent_name: string | null
  cycle_number: number
  status: string
  current_phase: string | null  // perception, reasoning, action, completed, failed
  trigger_type: string | null
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  error_message: string | null
  // LLM usage fields
  llm_model: string | null
  tokens_input: number | null
  tokens_output: number | null
  llm_cost_usd: number | null
}

export interface HandsUpItem {
  id: number
  agent_id: string
  agent_name: string | null
  severity: string
  category: string | null
  reason: string | null
  created_at: string
  status: string
  execution_id: number | null
  cycle_number: number | null  // cycle that triggered the escalation
}

export interface OptimizerMonitoringResponse {
  summary: OptimizerMonitoringSummary
  agents: AgentStatusSummary[]
  recent_executions: ExecutionHistoryItem[]
  pending_hands_up: HandsUpItem[]
}

// API response type for paginated agents list
interface AgentsListResponse {
  agents: Agent[]
  total: number
}

export const agentsService = {
  async getAgents(organizationId: number): Promise<Agent[]> {
    const response = await api.get<Agent[] | AgentsListResponse>(`/agents?organization_id=${organizationId}`)
    // Handle both array response (old) and object response (new: {agents: [], total: 0})
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.agents || []
  },

  async getAgent(agentId: number): Promise<Agent> {
    const response = await api.get<Agent>(`/agents/${agentId}`)
    return response.data
  },

  async createAgent(organizationId: number, data: AgentCreateRequest): Promise<Agent> {
    const response = await api.post<Agent>(`/agents?organization_id=${organizationId}`, data)
    return response.data
  },

  async updateAgent(agentId: number, _organizationId: number, data: AgentUpdateRequest): Promise<Agent> {
    const response = await api.patch<Agent>(`/agents/${agentId}`, data)
    return response.data
  },

  async deleteAgent(agentId: number): Promise<{ deleted: boolean; agent_id: number }> {
    const response = await api.delete<{ deleted: boolean; agent_id: number }>(`/agents/${agentId}`)
    return response.data
  },

  async activateAgent(agentId: number): Promise<Agent> {
    const response = await api.post<Agent>(`/agents/${agentId}/activate`)
    return response.data
  },

  async pauseAgent(agentId: number): Promise<Agent> {
    const response = await api.post<Agent>(`/agents/${agentId}/pause`)
    return response.data
  },

  async runAgent(agentId: number, organizationId: number): Promise<AgentRunResponse> {
    const response = await api.post<AgentRunResponse>(`/agents/${agentId}/run?organization_id=${organizationId}`)
    return response.data
  },

  // Execution endpoints
  async getExecutions(agentId: number, status?: string, limit = 50): Promise<AgentExecution[]> {
    const params = new URLSearchParams({ agent_id: String(agentId), limit: String(limit) })
    if (status) params.append('status', status)
    const response = await api.get<AgentExecution[]>(`/executions?${params}`)
    return response.data
  },

  async getExecution(executionId: number): Promise<AgentExecution> {
    const response = await api.get<AgentExecution>(`/executions/${executionId}`)
    return response.data
  },

  // Get execution by agent slug and cycle number (new format)
  async getExecutionByCycle(agentSlug: string, cycleNumber: number): Promise<ExecutionByCycleResponse> {
    const response = await api.get<ExecutionByCycleResponse>(
      `/agents/executions/by-cycle?agent_id=${encodeURIComponent(agentSlug)}&cycle_number=${cycleNumber}`
    )
    return response.data
  },

  async getExecutionActions(executionId: number): Promise<AgentAction[]> {
    const response = await api.get<AgentAction[]>(`/executions/${executionId}/actions`)
    return response.data
  },

  async cancelExecution(executionId: number): Promise<{ id: number; status: string }> {
    const response = await api.post<{ id: number; status: string }>(`/executions/${executionId}/cancel`)
    return response.data
  },

  // DEPRECATED: Hands-Up and Action endpoints have been removed.
  // Agents are now pure functions that only produce outputs. The Optimizer decides actions.

  // Saved reports (data_reports) — used to populate the agent Dataset picker.
  // Mirrors reports.tsx: GET /data/reports returns { exports: SavedReport[] }.
  async getSavedReports(): Promise<SavedReport[]> {
    const response = await api.get<{ exports?: SavedReport[] }>('/data/reports')
    return response.data.exports || []
  },

  // Organization model endpoints (for metrics/primitives picker)
  async getOrganizationMetrics(organizationId: number, system?: string): Promise<OrganizationMetric[]> {
    const params = new URLSearchParams()
    if (system) params.append('system', system)
    const queryString = params.toString()
    const url = `/organizations/${organizationId}/metrics${queryString ? `?${queryString}` : ''}`
    const response = await api.get<OrganizationMetric[]>(url)
    return response.data
  },

  async getOrganizationDimensions(organizationId: number): Promise<OrganizationDimension[]> {
    const response = await api.get<OrganizationDimension[]>(`/organizations/${organizationId}/dimensions`)
    return response.data
  },

  async getOrganizationSystems(organizationId: number): Promise<string[]> {
    const response = await api.get<{ systems: string[] }>(`/organizations/${organizationId}/systems`)
    return response.data.systems
  },

  // Agent performance from optimizer
  async getAgentPerformance(agentId: number): Promise<AgentPerformance | null> {
    try {
      const response = await api.get<{ performance: AgentPerformance | null }>(`/agents/${agentId}/performance`)
      return response.data.performance
    } catch {
      // Return null if performance data is not available
      return null
    }
  },

  // Get performance for all agents in an organization
  async getAgentsPerformance(organizationId: number): Promise<Record<number, AgentPerformance>> {
    try {
      const response = await api.get<{ performance: Record<number, AgentPerformance> }>(
        `/agents/performance?organization_id=${organizationId}`
      )
      return response.data.performance || {}
    } catch (error: any) {
      // Silently return empty object if performance endpoint is not available
      // This can happen if the API hasn't been deployed with the new endpoint yet
      if (error?.response?.status === 404 || error?.response?.status === 422) {
        return {}
      }
      console.warn('Could not fetch agent performance:', error?.message || error)
      return {}
    }
  },

  // Get optimizer monitoring data (all agents, executions, escalations)
  async getOptimizerMonitoring(): Promise<OptimizerMonitoringResponse> {
    const response = await api.get<OptimizerMonitoringResponse>('/agents/monitoring')
    return response.data
  }
}
